import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { useLanguage } from "@/i18n/LanguageContext";
import { fetchPublicPaper, publicPaperPdfUrl, type ConferencePaperPublicDetail } from "./api";

export function PaperReaderPage() {
  const { paperId } = useParams<{ paperId: string }>();
  const { t, locale } = useLanguage();
  const [paper, setPaper] = useState<ConferencePaperPublicDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!paperId) return;
    let cancelled = false;
    setLoading(true);
    fetchPublicPaper(paperId)
      .then((p) => {
        if (!cancelled) setPaper(p);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : t.common.error);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [paperId, t.common.error]);

  if (loading) {
    return <p className="dn-muted">{t.common.loading}</p>;
  }
  if (error || !paper) {
    return (
      <div>
        <div className="dn-alert dn-alert-error">{error || t.common.error}</div>
        <Link className="dn-btn dn-btn-ghost" to="/papers">
          {t.papers.backToPapers}
        </Link>
      </div>
    );
  }

  const title = locale === "ar" && paper.title_alt ? paper.title_alt : paper.title;
  const edition =
    locale === "ar"
      ? `${paper.conference_title_ar} — ${paper.conference_year}`
      : `${paper.conference_title_en} — ${paper.conference_year}`;

  return (
    <div className="dn-reader">
      <nav className="dn-reader-breadcrumb" aria-label="breadcrumb">
        <Link to="/papers">{t.papers.title}</Link>
        <span aria-hidden="true"> / </span>
        <span>{edition}</span>
      </nav>

      <div className="dn-reader-header">
        <div>
          <h1>{title}</h1>
          <p className="dn-muted">{paper.authors}</p>
          {paper.is_external_archive && (
            <p className="dn-paper-archive-badge">{t.papers.externalArchive}</p>
          )}
        </div>
        <div className="dn-reader-actions">
          {paper.has_pdf && (
            <a className="dn-btn dn-btn-primary" href={publicPaperPdfUrl(paper.id, true)}>
              {t.papers.downloadPdf}
            </a>
          )}
          <Link className="dn-btn dn-btn-ghost" to="/papers">
            {t.papers.backToPapers}
          </Link>
        </div>
      </div>

      {paper.has_pdf ? (
        <div className="dn-reader-frame-wrap">
          <iframe
            className="dn-reader-frame"
            title={title}
            src={publicPaperPdfUrl(paper.id, false)}
          />
        </div>
      ) : (
        <div className="dn-card">
          <p>{t.papers.pdfUnavailable}</p>
        </div>
      )}
    </div>
  );
}
